import React from 'react';
import {
  Button,
  ButtonGroup,
  ButtonGroupProps,
  ButtonProps,
} from '@chakra-ui/react';
import { QIcon, IconNameType } from '../..';
import { QCounter } from '../Counter/Counter';

type BaseVariant = 'solid' | 'outline' | 'ghost' | 'link';
type CombinedVariant =
  | BaseVariant
  | `${BaseVariant}-dest`
  | `${BaseVariant}-ai`;

type CommonButtonProps = Pick<
  ButtonProps,
  | 'isLoading'
  | 'onClick'
  | 'size'
  | 'isDisabled'
  | 'type'
  | 'width'
  | 'isActive'
> & {
  variant?: BaseVariant;
  leftIcon?: IconNameType;
  rightIcon?: IconNameType;
  count?: number;
  children?: React.ReactNode;
};

// The `isDestructive` and `isAI` are mutually exclusive.
type StandardButtonProps = CommonButtonProps & {
  isDestructive?: false;
  isAI?: false;
};

type AIButtonProps = CommonButtonProps & {
  isAI: true;
  isDestructive?: false;
};
type DestructiveButtonProps = CommonButtonProps & {
  isAI?: false;
  isDestructive: true;
};

export type QButtonProps =
  | StandardButtonProps
  | AIButtonProps
  | DestructiveButtonProps;

export type QButtonGroupProps = Pick<
  ButtonGroupProps,
  'isDisabled' | 'size'
> & {
  children?: React.ReactNode;
};

export const QButton: React.FC<QButtonProps> = ({
  isDestructive = false,
  children,
  variant,
  leftIcon,
  rightIcon,
  isAI = false,
  count = 0,
  ...rest
}) => {
  let combinedVariant: CombinedVariant = variant ?? 'solid';
  if (isAI) {
    combinedVariant = `${combinedVariant}-ai`;
  } else if (isDestructive) {
    combinedVariant = `${combinedVariant}-dest`;
  }

  const countActive = count > 0;

  const counterVariant = (variant ?? 'solid') === 'solid' ? 'white' : 'default';

  const getLeftIcon = () => {
    if (isAI) {
      return <QIcon iconName="QualioAI" />;
    }
    if (countActive && !isDestructive) {
      return undefined;
    }
    if (leftIcon) {
      return <QIcon iconName={leftIcon} />;
    }
  };

  return (
    <Button
      {...rest}
      variant={combinedVariant}
      leftIcon={getLeftIcon()}
      rightIcon={
        !isAI && rightIcon ? <QIcon iconName={rightIcon} /> : undefined
      }
    >
      {countActive && !isDestructive && !isAI && (
        <QCounter count={count} size="sm" variant={counterVariant} mr={2} />
      )}
      {children || 'Button Label'}
    </Button>
  );
};
QButton.displayName = 'QButton';

export const QButtonGroup: React.FC<QButtonGroupProps> = (props) => {
  return <ButtonGroup {...props} />;
};
QButtonGroup.displayName = 'QButtonGroup';

export type QDestructiveButtonProps = Pick<
  QButtonProps,
  'onClick' | 'isLoading' | 'isDisabled'
> & {
  children?: React.ReactNode;
};
export const QDestructiveButton: React.FC<QDestructiveButtonProps> = (
  props,
) => <QButton variant="outline" isDestructive {...props} />;
QDestructiveButton.displayName = 'QDestructiveButton';
